import { useEffect, useState } from 'react'
import { Modal, Table, Button, Space, Select, Message, Popconfirm } from '@arco-design/web-react'
import {
  listAdminSubscriptionPlansApi,
  listUserSubscriptionsAdminApi,
  adminBindUserSubscriptionApi,
  adminInvalidateUserSubscriptionApi,
  adminDeleteUserSubscriptionApi,
  type SubscriptionPlanRow,
  type UserSubscriptionSummaryRow,
} from '@/api/subscription'
import type { AdminUser } from '@/api/user'
import { renderTimestamp, Pill } from '@/utils/render'
import { renderQuota } from '@/utils/quota'

interface Props {
  visible: boolean
  user: AdminUser | null
  onCancel: () => void
  onSuccess?: () => void
}

export default function UserSubscriptionsModal({ visible, user, onCancel, onSuccess }: Props) {
  const [loading, setLoading] = useState(false)
  const [subs, setSubs] = useState<UserSubscriptionSummaryRow[]>([])
  const [plans, setPlans] = useState<SubscriptionPlanRow[]>([])
  const [planId, setPlanId] = useState<number | undefined>()

  const load = async () => {
    if (!user?.id) return
    setLoading(true)
    try {
      const [p, s] = await Promise.all([
        listAdminSubscriptionPlansApi(),
        listUserSubscriptionsAdminApi(user.id),
      ])
      setPlans(p)
      setSubs(Array.isArray(s) ? s : [])
    } catch (e) {
      Message.error(e instanceof Error ? e.message : '加载失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!visible || !user) return
    setPlanId(undefined)
    void load()
  }, [visible, user?.id])

  const planTitle = (id: number) => plans.find((x) => x.plan?.id === id)?.plan?.title || `套餐 #${id}`

  const handleBind = async () => {
    if (!user?.id || !planId) {
      Message.warning('请选择套餐')
      return
    }
    setLoading(true)
    try {
      await adminBindUserSubscriptionApi(user.id, planId)
      Message.success('已绑定')
      await load()
      onSuccess?.()
    } catch (e) {
      Message.error(e instanceof Error ? e.message : '失败')
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    { title: '订阅 ID', width: 90, render: (_: unknown, r: UserSubscriptionSummaryRow) => r.subscription?.id },
    { title: '套餐', width: 180, render: (_: unknown, r: UserSubscriptionSummaryRow) => planTitle(r.subscription?.plan_id || 0) },
    {
      title: '额度',
      width: 160,
      render: (_: unknown, r: UserSubscriptionSummaryRow) => (
        <span>
          {renderQuota(r.subscription?.amount_used)} / {renderQuota(r.subscription?.amount_total)}
        </span>
      ),
    },
    {
      title: '状态',
      width: 100,
      render: (_: unknown, r: UserSubscriptionSummaryRow) => {
        const st = r.subscription?.status
        if (st === 'active') return <Pill variant="success">生效中</Pill>
        if (st === 'cancelled') return <Pill variant="neutral">已作废</Pill>
        return <Pill variant="warning">{st || '—'}</Pill>
      },
    },
    { title: '结束', width: 170, render: (_: unknown, r: UserSubscriptionSummaryRow) => renderTimestamp(r.subscription?.end_time) },
    {
      title: '操作',
      width: 200,
      render: (_: unknown, r: UserSubscriptionSummaryRow) => {
        const sid = r.subscription?.id
        if (!sid) return null
        return (
          <Space>
            <Popconfirm
              title="确定作废该订阅？"
              onOk={async () => {
                await adminInvalidateUserSubscriptionApi(sid)
                Message.success('已作废')
                await load()
                onSuccess?.()
              }}
            >
              <Button size="small" type="text">
                作废
              </Button>
            </Popconfirm>
            <Popconfirm
              title="确定删除该订阅记录？"
              onOk={async () => {
                await adminDeleteUserSubscriptionApi(sid)
                Message.success('已删除')
                await load()
                onSuccess?.()
              }}
            >
              <Button size="small" type="text" status="danger">
                删除
              </Button>
            </Popconfirm>
          </Space>
        )
      },
    },
  ]

  return (
    <Modal
      title={user ? `用户订阅：${user.username}` : '用户订阅'}
      visible={visible}
      onCancel={onCancel}
      footer={null}
      style={{ width: 900 }}
    >
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Select
          placeholder="选择套餐并绑定到该用户"
          style={{ width: 320 }}
          loading={loading}
          value={planId}
          onChange={(v) => setPlanId(v as number)}
          allowClear
        >
          {plans
            .filter((x) => x.plan?.enabled && x.plan.id)
            .map((r) => (
              <Select.Option key={r.plan.id} value={r.plan.id}>
                {r.plan.title} — ${r.plan.price_amount}
              </Select.Option>
            ))}
        </Select>
        <Button type="primary" loading={loading} onClick={handleBind}>
          绑定套餐
        </Button>
      </div>
      <Table
        rowKey={(r) => String(r.subscription?.id || Math.random())}
        loading={loading}
        columns={columns}
        data={subs}
        pagination={{ pageSize: 8 }}
      />
    </Modal>
  )
}
